import AlbumUploadButton from './components/AlbumUploadButton';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Link, NavLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TeamProvider } from './contexts/TeamContext';
import { UploadProvider, useUpload } from './contexts/UploadContext';
import UploadIndicator from './components/UploadIndicator';
import { cacheStrategy, useCachedFetch } from './utils/cacheStrategy';
import { photoCache } from './utils/photoCache';
import { getPrivateCacheScope, subscribePrivateCacheScope } from './utils/privateCacheScope.js';
import useIsMobile from './utils/useIsMobile';
import UpdateNotification from './components/UpdateNotification';
import { apiFetch, api } from './utils/api';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ProviderClaim from './components/ProviderClaim';
import LandingPage from './components/LandingPage';
import RedeemPromo from './components/RedeemPromo';
import Privacy from './components/Privacy';
import Security from './components/Security';
import DaileyCloud from './components/DaileyCloud';
import AnalogTalk from './components/AnalogTalk';
// YouAreTheProduct removed for public launch — see git history
import Guide from './components/Guide';
import './styles-landing.css';
import BatchUploader, { SharedUploadProvider } from './components/SharedUploadSession';
import ImageGrid from './components/ImageGrid';
import PageDropZone from './components/PageDropZone';
import PageHelp from './components/PageHelp';
import PublicGallery from './components/PublicGallery';
import ClientGallery from './components/ClientGallery';
import AlbumDetail from './components/AlbumDetail';
import SharesManager from './components/SharesManager';
import Favorites from './components/Favorites';
import Settings from './components/Settings';
import Watermark from './components/Watermark';
import MobileNav from './components/MobileNav';
import PageHero from './components/PageHero';
import AppRail from './components/AppRail';
import Tags from './components/Tags';
import TagSelectorModal from './components/TagSelectorModal';
import FloatingActionButton from './components/FloatingActionButton';
import ScrollToTop from './components/ScrollToTop';
import Friends from './components/Friends';
import TheStrip from './components/TheStrip';
import Vault from './components/Vault';
import VaultCollection from './components/VaultCollection';
import ScanArchive from './components/ScanArchive';
import ScanArchiveMonth from './components/ScanArchiveMonth';
import ScanArchiveFolder from './components/ScanArchiveFolder';
import { PhotoIcon, AlbumIcon, ShareIcon, SettingsIcon } from './components/Icons';
// ThemeToggle temporarily disabled
import RouteTransition from './components/RouteTransition';
import FriendsManager from './components/FriendsManager';
import Submissions from './components/Submissions';
import JobDetail from './components/JobDetail';
import { FiCamera, FiLink, FiShield, FiX, FiUsers, FiDroplet, FiTag, FiPlus, FiSquare, FiEdit2, FiTrash2, FiSearch, FiFilter, FiInfo, FiRefreshCw } from 'react-icons/fi';
import AdminDashboard from './components/AdminDashboard';
import { FaHeart } from 'react-icons/fa';
import { showToast } from './components/NotificationTray';
import { SkeletonPhotoGrid, SkeletonAlbumGrid, SkeletonMobilePhotoGrid, SkeletonFullPage } from './components/Skeleton';
import CoreInfo from './components/CoreInfo';
import ClientPortal from './components/ClientPortal';
import BugReportButton from './components/BugReportButton';
import StorageWarningBanner from './components/StorageWarningBanner';
import Paywall from './components/Paywall';
import ClientGalleryViewer from './components/ClientGalleryViewer';
import ClientSelectionFlow from './components/ClientSelectionFlow';
import { getFeatureFlags } from './utils/featureFlags';
import LibraryFilterPanel from './components/LibraryFilterPanel';
import { metadataSyncManager } from './utils/metadataSyncManager';
import { SUPPORTED_EXTENSIONS_TEXT } from './utils/supportedExtensions';

// Add to Album modal — visual album picker with cover thumbnails
function AddToAlbumModal({ albums, selectedCount, onSelect, onClose }) {
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    setTimeout(() => searchRef.current?.focus(), 80);
  }, []);

  const filtered = albums.filter(a =>
    !search || a.title?.toLowerCase().includes(search.toLowerCase())
  );

  async function handleSelect(albumId) {
    setAdding(albumId);
    await onSelect(albumId);
  }

  function getThumbSrc(album) {
    const file = album.cover_image || album.preview_images?.[0];
    if (!file) return null;
    const token = window?.localStorage?.getItem('auth_token');
    const q = '';
    return `/media/variants/sm/${file.replace(/\.[^.]+$/, '.jpg')}${q}`;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="atam-modal" onClick={e => e.stopPropagation()}>
        <div className="atam-header">
          <div>
            <h3 className="atam-title">Add to Album</h3>
            <p className="atam-sub">{selectedCount} {selectedCount === 1 ? 'photo' : 'photos'} selected</p>
          </div>
          <button className="atam-close" onClick={onClose} aria-label="Close">
            <FiX size={20} />
          </button>
        </div>

        {albums.length > 5 && (
          <div className="atam-search">
            <FiSearch size={16} />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search albums..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        )}

        <div className="atam-list">
          {filtered.length === 0 && (
            <div className="atam-empty">
              {search ? `No albums match "${search}"` : 'No albums yet'}
            </div>
          )}
          {filtered.map(album => {
            const thumbSrc = getThumbSrc(album);
            const isAdding = adding === album.id;
            return (
              <button
                key={album.id}
                className={`atam-item${isAdding ? ' atam-item--adding' : ''}`}
                onClick={() => handleSelect(album.id)}
                disabled={!!adding}
              >
                <div className="atam-thumb">
                  {thumbSrc ? (
                    <img src={thumbSrc} alt="" loading="lazy" />
                  ) : (
                    <AlbumIcon size={22} />
                  )}
                </div>
                <div className="atam-info">
                  <span className="atam-name">{album.title || 'Untitled Album'}</span>
                  <span className="atam-count">{album.image_count || 0} photos</span>
                </div>
                {isAdding && (
                  <div className="atam-spinner" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Album card with hover preview effect
function renderAlbumShareBadges(album, { compact = false } = {}) {
  if (!album) return null;

  const stripCount = Number(album.strip_share_count || 0);
  const clientCount = Number(album.client_share_count || 0);
  const publicCount = Number(album.public_share_count || 0);
  const hasStatus = stripCount > 0 || clientCount > 0 || publicCount > 0;

  if (!hasStatus) return null;

  const stripLabel = compact ? 'Strip' : 'On Strip';
  const clientLabel = compact ? 'Client' : 'Client gallery';
  const publicLabel = compact ? 'Public' : 'Public link';

  return (
    <div className={`album-share-status${compact ? ' compact' : ''}`}>
      {stripCount > 0 && (
        <span className="album-share-pill strip" title="Shared to The Strip">
          <FiCamera size={12} />
          {stripLabel}{stripCount > 1 ? ` (${stripCount})` : ''}
        </span>
      )}
      {clientCount > 0 && (
        <span className="album-share-pill client" title="Client gallery sharing enabled">
          <FiUsers size={12} />
          {clientLabel}{clientCount > 1 ? ` (${clientCount})` : ''}
        </span>
      )}
      {publicCount > 0 && (
        <span className="album-share-pill public" title="Public share link active">
          <FiLink size={12} />
          {publicLabel}{publicCount > 1 ? ` (${publicCount})` : ''}
        </span>
      )}
    </div>
  );
}

function albumVisibilityLabel(album) {
  if (!album) return null;
  const publicCount = Number(album.public_share_count || 0);
  if (publicCount > 0) return 'public';
  if (album.team_visible) return 'team';
  return album.visibility || null;
}

function AlbumCard({ album, isFavorites = false, favoriteCount = 0, favoriteCover = null }) {
  const isMobile = useIsMobile();
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isPreviewActive, setIsPreviewActive] = useState(false);
  const cardRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const initialMousePosRef = useRef(null);

  const previewImages = isFavorites ? [] : (album?.preview_images || []);
  const hasMultipleImages = previewImages.length > 1;

  const handleMouseMove = useCallback((e) => {
    if (!hasMultipleImages || !cardRef.current) return;

    // Store initial mouse position
    if (!initialMousePosRef.current) {
      initialMousePosRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    // Only activate preview after delay AND mouse has moved significantly (10px)
    if (!isPreviewActive) {
      const dx = Math.abs(e.clientX - initialMousePosRef.current.x);
      if (dx < 10) return; // Not enough movement yet
    }

    if (!isPreviewActive) return; // Still waiting for delay

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newIndex = Math.min(
      Math.floor(percentage * previewImages.length),
      previewImages.length - 1
    );
    setPreviewIndex(newIndex);
  }, [hasMultipleImages, previewImages.length, isPreviewActive]);

  const handleMouseEnter = useCallback(() => {
    initialMousePosRef.current = null;
    // Start timer - preview activates after 500ms
    hoverTimerRef.current = setTimeout(() => {
      setIsPreviewActive(true);
    }, 500);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Clear timer and reset state
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsPreviewActive(false);
    setPreviewIndex(0);
    initialMousePosRef.current = null;
  }, []);

  const getImageSrc = () => {
    const token = window?.localStorage?.getItem('auth_token');
    const tokenParam = '';

    if (isFavorites) {
      if (!favoriteCover) return null;
      return `/media/variants/md/${favoriteCover.replace(/\.[^.]+$/, '.jpg')}${tokenParam}`;
    }

    const filename = isPreviewActive && hasMultipleImages && previewIndex > 0
      ? previewImages[previewIndex]
      : (album?.cover_image || previewImages[0]);

    if (!filename) return null;
    return `/media/variants/md/${filename.replace(/\.[^.]+$/, '.jpg')}${tokenParam}`;
  };

  const imageSrc = getImageSrc();
  const title = isFavorites ? 'Favorites' : album?.title;
  const count = isFavorites ? favoriteCount : album?.image_count;
  const badge = isFavorites ? 'automatic' : albumVisibilityLabel(album);
  const linkTo = isFavorites ? '/favorites' : `/albums/${album?.id}`;

  return (
    <Link
      ref={cardRef}
      className={`album-card ${isFavorites ? 'favorites' : ''}`}
      to={linkTo}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      <div className="album-card-image">
        {imageSrc ? (
          <img src={imageSrc} alt={title} loading="lazy" />
        ) : (
          <div className="placeholder">
            {isFavorites ? (
              <FaHeart size={48} className="text-[#81a49f] opacity-70" />
            ) : (
              <AlbumIcon size={48} className="text-gray-400 opacity-50" />
            )}
          </div>
        )}
        {/* Progress indicator dots - only show when preview is active */}
        {hasMultipleImages && isPreviewActive && (
          <div className="album-preview-dots">
            {previewImages.map((_, i) => (
              <div
                key={i}
                className={`album-preview-dot ${i === previewIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="album-card-content">
        <h3 className="album-card-title">
          {isFavorites && <FaHeart className="text-[#3e5c58]" />}
          {title}
        </h3>
        <div className="album-card-meta">
          <span className="album-card-count">{count} {count === 1 ? 'photo' : 'photos'}</span>
          {badge && <span className="album-card-badge">{badge}</span>}
        </div>
        {!isFavorites && renderAlbumShareBadges(album, { compact: isMobile })}
      </div>
    </Link>
  );
}

function Nav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const photosActive = location.pathname === '/' || location.pathname.startsWith('/photos');
  const features = getFeatureFlags(user);

  return (
    <nav className="sticky top-0 z-50 hidden bg-white border-b border-gray-200 shadow-sm md:block lg:hidden" role="navigation" aria-label="Primary">
      <div className="container flex items-center justify-between px-4 py-2 mx-auto">
        <Link to="/" className="flex items-center gap-2" aria-label="Photo Harbor Home">
          <img src="/favicon/favicon-96x96.png" alt="Photo Harbor" className="h-6 md:h-8" />
        </Link>
        <div className="items-center hidden gap-8 md:flex lg:hidden">
          <NavLink
            to="/photos"
            className={({ isActive }) => `flex items-center gap-2 text-gray-500 hover:text-gray-900 ${photosActive || isActive ? 'text-blue-500' : ''}`}
            aria-current={photosActive ? 'page' : undefined}
          >
            <PhotoIcon size={18} />
            <span>Photos</span>
          </NavLink>
          <NavLink
            to="/albums"
            className={({ isActive }) => `flex items-center gap-2 text-gray-500 hover:text-gray-900 ${isActive ? 'text-blue-500' : ''}`}
          >
            <AlbumIcon size={18} />
            <span>Albums</span>
          </NavLink>
          <NavLink
            to="/tags"
            className={({ isActive }) => `flex items-center gap-2 text-gray-500 hover:text-gray-900 ${isActive ? 'text-blue-500' : ''}`}
          >
            <FiTag size={18} />
            <span>Tags</span>
          </NavLink>
          {features.shares && (
            <NavLink
              to="/shares"
              className={({ isActive }) => `flex items-center gap-2 text-gray-500 hover:text-gray-900 ${isActive ? 'text-blue-500' : ''}`}
            >
              <ShareIcon size={18} />
              <span>Shares</span>
            </NavLink>
          )}
          <NavLink
            to="/watermark"
            className={({ isActive }) => `flex items-center gap-2 text-gray-500 hover:text-gray-900 ${isActive ? 'text-blue-500' : ''}`}
          >
            <FiDroplet size={18} />
            <span>Watermark</span>
          </NavLink>
          <NavLink
            to="/settings"
            className={({ isActive }) => `flex items-center gap-2 text-gray-500 hover:text-gray-900 ${isActive ? 'text-blue-500' : ''}`}
          >
            <SettingsIcon size={18} />
            <span>Settings</span>
          </NavLink>
          {user && user.role === 'super_admin' && (
            <NavLink
              to="/admin"
              className={({ isActive }) => `flex items-center gap-2 text-gray-500 hover:text-gray-900 ${isActive ? 'text-blue-500' : ''}`}
            >
              <FiShield size={18} />
              <span>Admin</span>
            </NavLink>
          )}
        </div>
        {/* User info - hidden on mobile, shown on tablet */}
        <div className="items-center hidden gap-4 md:flex lg:hidden">
          {user && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <span className="block text-sm font-medium text-gray-700">{user.display_name || user.username}</span>
                <span className="block text-xs text-gray-500">Secured by CORE</span>
              </div>
              <button onClick={logout} className="p-2 text-gray-500 rounded-full hover:bg-gray-100 hover:text-gray-700">
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <path fill="currentColor" d="M12 4L10.6 5.4 12.2 7H5v2h7.2l-1.6 1.6L12 12l4-4-4-4zm-8 8H2V2h2v10z" />
                </svg>
                <span className="sr-only">Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

function Placeholder({ title, children }) {
  return (
    <section className="card">
      <h2>{title}</h2>
      <p>{children}</p>
    </section>
  );
}

function SupportedFormatsInfo() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="supported-formats-info" ref={ref}>
      <button
        className="supported-formats-btn"
        onClick={() => setOpen(v => !v)}
        title="Supported file formats"
      >
        <FiInfo size={14} />
        <span>Supported formats</span>
      </button>
      {open && (
        <div className="supported-formats-popover">
          <div className="supported-formats-popover-title">Supported Uploads</div>
          <p className="supported-formats-list">{SUPPORTED_EXTENSIONS_TEXT}</p>
        </div>
      )}
    </div>
  );
}

function AllPhotos() {
  const { user } = useAuth();
  const cacheIdentity = getPrivateCacheScope().scope;
  const uploadContext = useUpload();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({});
  const [libraryFilters, setLibraryFilters] = useState({
    fileTypes: null,
    camera: null,
    lens: null,
    focalMin: null,
    focalMax: null,
    isoMin: null,
    isoMax: null,
    dateFrom: null,
    dateTo: null
  });

  // Check if any library filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(
      libraryFilters.fileTypes?.length ||
      libraryFilters.camera ||
      libraryFilters.lens ||
      libraryFilters.focalMin != null ||
      libraryFilters.focalMax != null ||
      libraryFilters.isoMin != null ||
      libraryFilters.isoMax != null ||
      libraryFilters.dateFrom ||
      libraryFilters.dateTo
    );
  }, [libraryFilters]);

  // Compute file type counts from current items to grey out empty types when filters active
  const hasNonFileTypeFilters = useMemo(() => {
    return !!(
      libraryFilters.camera ||
      libraryFilters.lens ||
      libraryFilters.focalMin != null ||
      libraryFilters.focalMax != null ||
      libraryFilters.isoMin != null ||
      libraryFilters.isoMax != null ||
      libraryFilters.dateFrom ||
      libraryFilters.dateTo
    );
  }, [libraryFilters]);

  const filteredFileTypeCounts = useMemo(() => {
    if (!hasNonFileTypeFilters || !items?.length) return null;
    const counts = {};
    items.forEach(img => {
      const ext = img.file_extension?.toLowerCase();
      if (ext) counts[ext] = (counts[ext] || 0) + 1;
    });
    return counts;
  }, [items, hasNonFileTypeFilters]);

  const [photosFilter, setPhotosFilter] = useState('all'); // all | favorites
  const [q, setQ] = useState('');
  const [showUploader, setShowUploader] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalImages, setTotalImages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [libraryLoadError, setLibraryLoadError] = useState('');
  const [libraryEmptyConfirmed, setLibraryEmptyConfirmed] = useState(false);
  const [actionLabel, setActionLabel] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'large', or 'list'
  const [tileMin, setTileMin] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('photoTileMin'), 10);
      if (!isNaN(v) && v >= 120 && v <= 480) return v;
    } catch { }
    return 200;
  });
  const [selectionMode, setSelectionMode] = useState(false);
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showAlbumSelector, setShowAlbumSelector] = useState(false);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('photosPageSize'), 10);
      return v === 100 ? 100 : 50;
    } catch {
      return 50;
    }
  });
  const pageCursorsRef = useRef({});
  const cursorKeyRef = useRef('');
  const refreshRequestRef = useRef(0);
  useEffect(() => {
    photoCache.sweep().catch(() => { });
  }, []);

  // Initialize metadata sync manager for library filtering
  useEffect(() => {
    metadataSyncManager.initialize().catch(console.error);
    return () => metadataSyncManager.destroy();
  }, []);

  // Update API filters when library filters change
  useEffect(() => {
    const newFilters = {};

    if (libraryFilters.fileTypes?.length) {
      // Remove leading dots and join with comma
      newFilters.file_type = libraryFilters.fileTypes.map(t => t.replace(/^\./, '')).join(',');
    }
    if (libraryFilters.camera) {
      newFilters.camera = libraryFilters.camera;
    }
    if (libraryFilters.lens) {
      newFilters.lens = libraryFilters.lens;
    }
    if (libraryFilters.focalMin != null) {
      newFilters.focal_min = libraryFilters.focalMin;
    }
    if (libraryFilters.focalMax != null) {
      newFilters.focal_max = libraryFilters.focalMax;
    }
    if (libraryFilters.isoMin != null) {
      newFilters.iso_min = libraryFilters.isoMin;
    }
    if (libraryFilters.isoMax != null) {
      newFilters.iso_max = libraryFilters.isoMax;
    }
    if (libraryFilters.dateFrom) {
      newFilters.date_from = libraryFilters.dateFrom;
    }
    if (libraryFilters.dateTo) {
      newFilters.date_to = libraryFilters.dateTo;
    }

    setFilters(newFilters);
    setCurrentPage(1);
  }, [libraryFilters]);

  // ImageGrid warms full-size photos on intent and around the active viewer.
  // Warming an entire page here competes with the visible thumbnails.
  const markCdnFull = (list = []) => (Array.isArray(list) ? list.map((img) => {
    if (img?._cdn_full) return img;
    if (img?.cdn_urls?.xl) return { ...img, _cdn_full: true };
    return img;
  }) : []);

  async function refresh(forceRefresh = false, options = {}) {
    const { silent = false } = options;
    const requestId = ++refreshRequestRef.current;
    const isCurrentRequest = () => refreshRequestRef.current === requestId;

    if (!silent && !initialLoad && items.length > 0 && !actionLabel) {
      setPageLoading(true);
    }
    setLoading(true);
    setLibraryLoadError('');
    const base = { ...filters };
    if (photosFilter === 'favorites') base.is_favorite = true;
    if (q) base.q = q;
    const cursorKey = JSON.stringify({ base, itemsPerPage });
    if (cursorKeyRef.current !== cursorKey) {
      cursorKeyRef.current = cursorKey;
      pageCursorsRef.current = {};
    }
    const cursorForPage = currentPage > 1 ? pageCursorsRef.current[currentPage] : null;
    const offset = (currentPage - 1) * itemsPerPage;
    // Use smaller variants when filters are active for faster loading
    const variantSizes = hasActiveFilters ? 'md,sm' : 'xl,lg';
    const params = new URLSearchParams({ ...base, limit: itemsPerPage, fields: 'compact', variants: variantSizes });
    if (cursorForPage) {
      params.set('cursor', cursorForPage);
    } else {
      params.set('offset', offset);
    }
    if (currentPage === 1) {
      params.set('include_total', 'true');
    }
    // Build a cache key that reflects all query-affecting inputs
    const cacheKey = `images_${photosFilter}_${q || ''}_${currentPage}_${itemsPerPage}_${JSON.stringify(base)}_${cursorForPage ? `c:${cursorForPage}` : `o:${offset}`}`;

    try {
      // Check cache first unless forcing refresh
      if (!forceRefresh && user && cacheIdentity) {
        const cached = await cacheStrategy.get('recentImages', { id: cacheKey, userId: cacheIdentity });
        if (cached && cached.fresh) {
          const cachedImages = markCdnFull(cached.data.images || cached.data);

          if (!isCurrentRequest()) return;
          setItems(cachedImages);
          const cachedTotal = typeof cached.data.total === 'number' ? cached.data.total : totalImages;
          setTotalImages(cachedTotal || cachedImages.length);
          setLibraryEmptyConfirmed((cachedTotal || cachedImages.length) === 0 && cachedImages.length === 0);
          if (cached.data.next_cursor && currentPage >= 1) {
            pageCursorsRef.current[currentPage + 1] = cached.data.next_cursor;
          }

          setLoading(false);
          setInitialLoad(false);
          return;
        } else if (cached) {
          // Use stale data while fetching
          const cachedImages = markCdnFull(cached.data.images || cached.data);

          if (!isCurrentRequest()) return;
          setItems(cachedImages);
          const cachedTotal = typeof cached.data.total === 'number' ? cached.data.total : totalImages;
          setTotalImages(cachedTotal || cachedImages.length);
          setLibraryEmptyConfirmed(false);
          if (cached.data.next_cursor && currentPage >= 1) {
            pageCursorsRef.current[currentPage + 1] = cached.data.next_cursor;
          }
          setInitialLoad(false);

        }
      }

      // Add cache-busting param when forcing refresh to bypass Service Worker cache
      if (forceRefresh) {
        params.set('_t', Date.now());
      }
      const res = await apiFetch(`/api/images?${params.toString()}`);
      if (!isCurrentRequest()) return;
      if (!res.ok) {
        throw new Error(`Failed to load images (${res.status})`);
      }

      const data = await res.json();
      const imagesPayload = markCdnFull(Array.isArray(data?.images)
        ? data.images
        : Array.isArray(data)
          ? data
          : []);
      const totalCount = typeof data?.total === 'number'
        ? data.total
        : (totalImages || imagesPayload.length);
      if (!isCurrentRequest()) return;
      if (data?.next_cursor) {
        pageCursorsRef.current[currentPage + 1] = data.next_cursor;
      }

      setItems(imagesPayload);
      setTotalImages(totalCount);
      setLibraryEmptyConfirmed(totalCount === 0 && imagesPayload.length === 0);

      if (user && cacheIdentity) {
        const cachePayload = { images: imagesPayload, total: totalCount, next_cursor: data?.next_cursor || null };
        cacheStrategy.set('recentImages', cachePayload, { id: cacheKey, userId: cacheIdentity });
      }

      if (user && cacheIdentity && currentPage === 1 && data?.next_cursor && !navigator?.connection?.saveData) {
        const nextPage = currentPage + 1;
        const nextCacheKey = `images_${photosFilter}_${q || ''}_${nextPage}_${itemsPerPage}_${JSON.stringify(base)}_c:${data.next_cursor}`;
        const alreadyCached = await cacheStrategy.get('recentImages', { id: nextCacheKey, userId: cacheIdentity });
        if (!alreadyCached) {
          const prefetchParams = new URLSearchParams({
            ...base,
            limit: itemsPerPage,
            fields: 'compact',
            variants: 'xl,lg',
            cursor: data.next_cursor
          });
          const prefetch = async () => {
            try {
              const prefRes = await apiFetch(`/api/images?${prefetchParams.toString()}`);
              if (!prefRes.ok) return;
              const prefData = await prefRes.json();
              const prefImages = Array.isArray(prefData?.images) ? prefData.images : [];
              cacheStrategy.set(
                'recentImages',
                { images: prefImages, total: totalCount, next_cursor: prefData?.next_cursor || null },
                { id: nextCacheKey, userId: cacheIdentity }
              );
              if (prefData?.next_cursor) {
                pageCursorsRef.current[nextPage + 1] = prefData.next_cursor;
              }
            } catch {}
          };
          if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            window.requestIdleCallback(() => prefetch());
          } else {
            setTimeout(prefetch, 800);
          }
        }
      }
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error('Failed to refresh library:', error);
      setLibraryLoadError(error?.message || 'Library refresh failed');
      setLibraryEmptyConfirmed(false);
    } finally {
      if (!isCurrentRequest()) return;
      setLoading(false);
      if (!silent) {
        setPageLoading(false);
      }
      setInitialLoad(false);
    }
  }

  useEffect(() => {
    refresh();
    window.scrollTo(0, 0);
  }, [JSON.stringify(filters), currentPage, photosFilter, itemsPerPage]);

  // Live search with debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      refresh(true);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Load albums when showing album selector
  useEffect(() => {
    if (showAlbumSelector) {
      apiFetch('/api/albums')
        .then(r => r.json())
        .then(setAlbums)
        .catch(console.error);
    }
  }, [showAlbumSelector]);

  // Load tags when showing tag selector
  useEffect(() => {
    if (showTagSelector) {
      apiFetch('/api/tags')
        .then(r => r.json())
        .then(setAvailableTags)
        .catch(console.error);
    }
  }, [showTagSelector]);

  async function addToAlbum(albumId) {
    try {
      for (const imageId of selectedImages) {
        await api.post(`/api/albums/${albumId}/images`, { image_id: imageId });
      }
      const album = albums.find(a => a.id === albumId);
      showToast(`Added ${selectedImages.length} photos to ${album?.title || 'album'}`, '', 'success');
      setSelectionMode(false);
      setSelectedImages([]);
      setShowAlbumSelector(false);
    } catch (error) {
      console.error('Failed to add to album:', error);
      showToast('Failed to add photos to album', '', 'error');
    }
  }

  async function addTagToSelected(tagId) {
    try {
      for (const imageId of selectedImages) {
        await fetch(`/api/images/${imageId}/tags`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ tag_id: tagId })
        });
      }
      showToast(`Added tag to ${selectedImages.length} photos`, '', 'success');
      setSelectionMode(false);
      setSelectedImages([]);
      setShowTagSelector(false);
    } catch (error) {
      console.error('Failed to add tag:', error);
      showToast('Failed to add tag to photos', '', 'error');
    }
  }

  async function deleteSelectedPhotos() {
    if (!confirm(`Delete ${selectedImages.length} photos? This cannot be undone.`)) return;

    const idsToDelete = [...selectedImages];

    // Optimistically remove from UI immediately
    setItems(prev => prev.filter(img => !idsToDelete.includes(img.id)));
    setTotalImages(prev => Math.max(0, prev - idsToDelete.length));
    setSelectionMode(false);
    setSelectedImages([]);

    // Batch delete on the server (limited concurrency, single request)
    fetch('/api/images/batch-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ids: idsToDelete })
    }).then(async (resp) => {
      if (!resp.ok) console.error('Batch delete failed:', resp.status);
      // Invalidate caches
      if (user && cacheIdentity) {
        idsToDelete.forEach(imageId => {
          cacheStrategy.invalidate('delete', { userId: cacheIdentity, imageId });
        });
      }
      refresh(true);
    }).catch(err => {
      console.error('Batch delete error:', err);
      refresh(true);
    });
  }

  function handleUploadComplete(counts) {
    if (user && cacheIdentity) {
      cacheStrategy.invalidate('upload', { userId: cacheIdentity });
    }
    refresh(true, { silent: items.length > 0 }); // Force refresh, but avoid washing out the grid if it's already loaded
    if (counts.hasProcessing) {
      showToast('Uploads complete', 'Your library will update automatically while previews and metadata finish processing.', 'info');
    }
    if (counts.autoClose) {
      setShowUploader(false);
      setDroppedFiles([]);
    }
  }

  const mobileUploadRefreshRef = useRef(handleUploadComplete);
  mobileUploadRefreshRef.current = handleUploadComplete;
  useEffect(() => {
    const refreshMobileUpload = event => {
      if (event.detail?.scope !== getPrivateCacheScope().scope) return;
      mobileUploadRefreshRef.current(event.detail.counts || {});
    };
    window.addEventListener('photos:mobile-upload-updated', refreshMobileUpload);
    return () => window.removeEventListener('photos:mobile-upload-updated', refreshMobileUpload);
  }, []);

  function handleFilesDropped(files) {
    if (uploadContext?.uploading) return;
    setDroppedFiles(files);
    setShowUploader(true);
  }

  // Auto-upload dropped files
  useEffect(() => {
    if (droppedFiles.length > 0 && showUploader) {
      // The BatchUploader will handle these files
    }
  }, [droppedFiles, showUploader]);

  const totalPages = Math.ceil(totalImages / itemsPerPage);

  const isMobile = useIsMobile();
  // Only show skeleton on initial page load, never when filtering (to avoid jumpiness)
  const showSkeleton = initialLoad && items.length === 0;
  const showLibraryUnavailable = !showSkeleton && items.length === 0 && !libraryEmptyConfirmed;
  const isBusy = pageLoading || Boolean(actionLabel);
  const busyLabel = actionLabel || 'Loading photos...';

  const photoStats = [
    { label: 'Photos', value: totalImages },
    { label: 'Showing', value: items.length }
  ];

  return (
    <PageDropZone onFilesDropped={handleFilesDropped}>
      <PageHero
        eyebrow="Library"
        title="All Photos"
        stats={photoStats}
      />
      <div className="page-toolbar">
        <PageHelp
          title="How to use All Photos"
          description="Browse your full library, search fast, and take action in bulk."
          tips={[
            'Use search and filters to find photos quickly.',
            'Turn on selection mode to tag, move, or delete multiple items.',
            'Drag and drop files anywhere on the page to upload.'
          ]}
          subject="Photo Harbor Help - All Photos"
        />
        <SupportedFormatsInfo />
      </div>

      {/* Mobile FAB with integrated selection controls */}
      {isMobile && (
        <FloatingActionButton
          onUpload={() => setShowUploader(true)}
          onSelect={() => setSelectionMode(true)}
          isSelectionMode={selectionMode}
          selectedCount={selectedImages.length}
          onCancelSelection={() => {
            setSelectionMode(false);
            setSelectedImages([]);
            setShowSelectionMenu(false);
          }}
          onTagSelected={() => setShowTagSelector(true)}
          onAddToAlbum={() => setShowAlbumSelector(true)}
          onDeleteSelected={deleteSelectedPhotos}
        />
      )}

      <div className={`mx-auto ${isMobile ? 'px-0 py-0' : 'px-6 py-6'}`}>
        <section
          className={`relative page-surface ${isMobile ? 'page-surface--tight' : 'page-surface--padded'}`}
          aria-busy={isBusy}
        >
          {isBusy && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-sm">
              <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-full shadow">
                <span className="processing-spinner text-blue-500" />
                <span>{busyLabel}</span>
              </div>
            </div>
          )}
          <div className="items-center justify-end hidden gap-4 md:flex">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
                <button className={`px-2 py-1 text-sm rounded-lg ${photosFilter === 'all' ? 'bg-white shadow' : ''}`} onClick={() => { setPhotosFilter('all'); setCurrentPage(1); }}>All</button>
                <button className={`px-2 py-1 text-sm rounded-lg ${photosFilter === 'favorites' ? 'bg-white shadow' : ''}`} onClick={() => { setPhotosFilter('favorites'); setCurrentPage(1); }}>Favorites</button>
              </div>
              <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  className={`px-2 py-1 text-sm rounded-lg ${itemsPerPage === 50 ? 'bg-white shadow' : ''}`}
                  onClick={() => { setItemsPerPage(50); setCurrentPage(1); try { localStorage.setItem('photosPageSize', '50'); } catch { } }}
                >
                  50
                </button>
                <button
                  className={`px-2 py-1 text-sm rounded-lg ${itemsPerPage === 100 ? 'bg-white shadow' : ''}`}
                  onClick={() => { setItemsPerPage(100); setCurrentPage(1); try { localStorage.setItem('photosPageSize', '100'); } catch { } }}
                >
                  100
                </button>
              </div>
              <div className="relative">
                <FiSearch className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                <input type="text" placeholder="Search photos..." value={q} onChange={(e) => setQ(e.target.value)} className="w-64 py-2 pl-10 pr-4 border border-gray-200 rounded-lg" />
                {q && (
                  <button
                    type="button"
                    className="absolute p-1 text-gray-400 transform -translate-y-1/2 rounded-full hover:bg-gray-100 right-2 top-1/2"
                    aria-label="Clear search"
                    onClick={() => { setQ(''); setCurrentPage(1); }}
                  >
                    <FiX aria-hidden />
                  </button>
                )}
              </div>
              {!selectionMode ? (
                <>
                  <button data-testid="open-uploader" onClick={() => setShowUploader(true)} className="px-4 py-2 text-white bg-blue-500 rounded-lg">Upload Photos</button>
                  <button onClick={() => setSelectionMode(true)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg">Select Images</button>
                </>
              ) : (
                <>
                  <span className="text-sm font-medium text-gray-700">{selectedImages.length} selected</span>
                  <button onClick={() => setShowTagSelector(true)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg" disabled={selectedImages.length === 0}>Tag Selected</button>
                  <button onClick={() => setShowAlbumSelector(true)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg" disabled={selectedImages.length === 0}>Add to Album</button>
                  <button onClick={deleteSelectedPhotos} className="px-4 py-2 text-white bg-red-500 rounded-lg" disabled={selectedImages.length === 0}>Delete Selected</button>
                  <button onClick={() => { setSelectionMode(false); setSelectedImages([]); }} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg">Cancel</button>
                </>
              )}
            </div>
          </div>

          {/* Image size row - hidden on mobile */}
          <div className="items-center hidden gap-2 mt-4 mb-2 md:flex">
            <label htmlFor="tile-size" className="text-sm text-gray-500">Image size</label>
            <input
              id="tile-size"
              type="range"
              min="120"
              max="480"
              step="1"
              value={tileMin}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setTileMin(v);
                try { localStorage.setItem('photoTileMin', String(v)); } catch { }
              }}
              className="w-48"
              aria-label="Photo size"
            />
          </div>

          {/* Library Filter Panel */}
          <LibraryFilterPanel
            filters={libraryFilters}
            onChange={setLibraryFilters}
            onClear={() => setLibraryFilters({
              fileTypes: null,
              camera: null,
              lens: null,
              focalMin: null,
              focalMax: null,
              isoMin: null,
              isoMax: null,
              dateFrom: null,
              dateTo: null
            })}
            filteredFileTypeCounts={filteredFileTypeCounts}
          />

          {showSkeleton ? (
            <SkeletonPhotoGrid count={36} tileSize={tileMin} />
          ) : items.length === 0 ? (
            // Show empty state immediately when no items (prevents layout jump)
            <div className="empty-state-container">
              {showLibraryUnavailable ? (
                <div className="empty-state-filters">
                  <div className="empty-state-icon">
                    <FiRefreshCw size={48} />
                  </div>
                  <h3>{libraryLoadError ? 'We could not refresh your library' : 'Syncing your library...'}</h3>
                  <p>
                    {libraryLoadError
                      ? 'Your photos are still stored safely. Retry the library refresh and Photo Harbor will reconnect.'
                      : 'Your photos are stored safely. Photo Harbor is reconnecting the gallery view now.'}
                  </p>
                  <button
                    className="empty-state-btn secondary"
                    onClick={() => refresh(true)}
                  >
                    Retry library
                  </button>
                </div>
              ) : hasActiveFilters ? (
                // No results due to filters
                <div className="empty-state-filters">
                  <div className="empty-state-icon">
                    <FiFilter size={48} />
                  </div>
                  <h3>No photos match your filters</h3>
                  <p>Try adjusting or clearing your filters to see more photos.</p>
                  <button
                    className="empty-state-btn secondary"
                    onClick={() => setLibraryFilters({
                      fileTypes: null,
                      camera: null,
                      lens: null,
                      focalMin: null,
                      focalMax: null,
                      isoMin: null,
                      isoMax: null,
                      dateFrom: null,
                      dateTo: null
                    })}
                  >
                    Clear all filters
                  </button>
                </div>
              ) : (
                // Empty gallery - no photos at all
                <div className="empty-state-welcome">
                  <div className="empty-state-icon">
                    <PhotoIcon size={64} />
                  </div>
                  <h3>Welcome to Your Photo Gallery!</h3>
                  <p>Start by uploading your first photos to build your collection.</p>
                  <button
                    className="empty-state-btn primary"
                    onClick={() => setShowUploader(true)}
                  >
                    <FiPlus /> Upload Photos
                  </button>
                </div>
              )}
            </div>
          ) : (
            <ImageGrid
              images={items}
              onRefresh={refresh}
              viewMode={'grid'}
              selectionMode={selectionMode}
              selectedImages={selectedImages}
              onSelectionChange={setSelectedImages}
              tileSize={tileMin}
              showProcessingToast
              useFastThumbnails={hasActiveFilters || undefined}
            />
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => {
                  setCurrentPage(1);
                }}
                disabled={currentPage === 1 || isBusy}
                className="px-3 py-1 text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => {
                  setCurrentPage(p => Math.max(1, p - 1));
                }}
                disabled={currentPage === 1 || isBusy}
                className="px-3 py-1 text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => {
                        setCurrentPage(pageNum);
                      }}
                      disabled={isBusy}
                      className={`px-3 py-1 rounded-lg ${currentPage === pageNum ? 'bg-blue-500 text-white' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100'} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => {
                  setCurrentPage(p => Math.min(totalPages, p + 1));
                }}
                disabled={currentPage === totalPages || isBusy}
                className="px-3 py-1 text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => {
                  setCurrentPage(totalPages);
                }}
                disabled={currentPage === totalPages || isBusy}
                className="px-3 py-1 text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>

              {totalPages > 5 && (
                <div className="flex items-center gap-1 ml-2 pl-2 border-l border-gray-200">
                  <span className="text-sm text-gray-500">Go to</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    placeholder={currentPage}
                    className="w-16 px-2 py-1 text-center text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const page = parseInt(e.target.value, 10);
                        if (page >= 1 && page <= totalPages) {
                          setCurrentPage(page);
                          e.target.value = '';
                          e.target.blur();
                        }
                      }
                    }}
                    disabled={isBusy}
                  />
                  <span className="text-sm text-gray-400">of {totalPages}</span>
                </div>
              )}
            </div>
          )}

          {showUploader && <BatchUploader
            onUploadComplete={handleUploadComplete}
            onRequestClose={() => { setShowUploader(false); setDroppedFiles([]); }}
            initialFiles={droppedFiles}
          />}

          {showAlbumSelector && (
            <AddToAlbumModal
              albums={albums}
              selectedCount={selectedImages.length}
              onSelect={addToAlbum}
              onClose={() => setShowAlbumSelector(false)}
            />
          )}

          {showTagSelector && (
            <TagSelectorModal
              selectedImages={selectedImages}
              onClose={() => setShowTagSelector(false)}
              onTagSelected={addTagToSelected}
              availableTags={availableTags}
              onTagsUpdated={(newTags) => setAvailableTags(newTags)}
            />
          )}
        </section>
      </div>
    </PageDropZone>
  );
}

function Albums() {
  const { user } = useAuth();
  const cacheIdentity = getPrivateCacheScope().scope;
  const [albums, setAlbums] = useState([]);
  const [albumQuery, setAlbumQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid | list
  const [sortMode, setSortMode] = useState('recent'); // recent | az
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [favoriteCover, setFavoriteCover] = useState(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    async function loadAlbums() {
      // Check cache first
      if (user && cacheIdentity) {
        const cachedAlbums = await cacheStrategy.get('albums', { userId: cacheIdentity });
        const cachedFavorites = await cacheStrategy.get('favorites', { userId: cacheIdentity });

        if (cachedAlbums) {
          setAlbums(cachedAlbums.data);
          setLoading(false);
          if (cachedFavorites) {
            const favs = cachedFavorites.data.images || cachedFavorites.data;
            setFavoriteCount(Array.isArray(favs) ? favs.length : 0);
            if (Array.isArray(favs) && favs.length > 0) {
              setFavoriteCover(favs[0].filename);
            }
          }
          if (cachedAlbums.fresh && cachedFavorites?.fresh) {
            return; // Both are fresh, no need to fetch
          }
        }
      }

      // Fetch fresh data
      try {
        const [albumsData, favResponse] = await Promise.all([
          apiFetch('/api/albums').then((r) => r.json()),
          apiFetch('/api/images?is_favorite=true&limit=10&fields=compact&variants=xl,lg').then((r) => r.json())
        ]);

        setAlbums(albumsData);
        const favs = favResponse.images || favResponse;
        setFavoriteCount(Array.isArray(favs) ? favs.length : 0);
        if (Array.isArray(favs) && favs.length > 0) {
          setFavoriteCover(favs[0].filename);
        }

        // Cache the results
        if (user && cacheIdentity) {
          cacheStrategy.set('albums', albumsData, { userId: cacheIdentity });
          cacheStrategy.set('favorites', favResponse, { userId: cacheIdentity });
        }
      } catch (error) {
        console.error('Failed to load albums:', error);
      } finally {
        setLoading(false);
      }
    }

    loadAlbums();
  }, [user]);

  async function renameAlbum(id, newTitle) {
    try {
      const res = await fetch(`/api/albums/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: newTitle })
      });
      if (res.ok) {
        setAlbums(prev => prev.map(a => a.id === id ? { ...a, title: newTitle } : a));
        showToast('Album renamed', 'Title updated successfully.', 'success');
      }
    } catch (e) {
      console.error('Rename failed', e);
    }
  }

  async function deleteAlbum(id) {
    if (!confirm('Delete this album? Photos remain in your library.')) return;
    try {
      const res = await apiFetch(`/api/albums/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setAlbums(prev => prev.filter(a => a.id !== id));
        showToast('Album deleted', 'Removed from your albums.', 'info');
      }
    } catch (e) {
      console.error('Delete failed', e);
    }
  }

  async function createAlbum() {
    if (!title) return;
    try {
      const response = await fetch('/api/albums', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title })
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if this is an album limit error
        if (response.status === 403 && data.error === 'Album limit reached') {
          showToast(
            'Album limit reached',
            `You've reached the maximum of ${data.limit} albums on your ${data.tier || 'current'} plan. Go to Settings > Billing to upgrade for unlimited albums.`,
            'error'
          );
          return;
        }
        const message = data?.message || data?.error || 'Failed to create album';
        showToast('Album not created', message, 'error');
        return;
      }

      if (!data?.id) {
        showToast('Album not created', 'Failed to create album', 'error');
        return;
      }

      // Invalidate cache after creating album
      if (user && cacheIdentity) {
        cacheStrategy.invalidate('createAlbum', { userId: cacheIdentity });
      }

      setTitle('');
      nav(`/albums/${data.id}`);
    } catch (error) {
      showToast('Album not created', error?.message || 'Failed to create album', 'error');
    }
  }

  function sorted(albumsList) {
    const list = Array.isArray(albumsList) ? [...albumsList] : [];
    if (sortMode === 'az') {
      return list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }
    // recent: prefer created_at desc, fallback to id desc
    return list.sort((a, b) => {
      const ad = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
      const bd = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
      return bd - ad;
    });
  }

  const filteredAlbums = useMemo(() => {
    const query = albumQuery.trim().toLowerCase();
    const list = sorted(albums);
    if (!query) return list;
    return list.filter((album) => (album.title || '').toLowerCase().includes(query));
  }, [albums, albumQuery, sortMode]);
  const showFavorites = !albumQuery.trim();

  return (
    <>
      <PageHero
        eyebrow="Collections"
        title="Albums"
        stats={[
          { label: 'Albums', value: albums.length },
          { label: 'Public', value: albums.filter(a => Number(a.public_share_count || 0) > 0).length },
          { label: 'Strip', value: albums.filter(a => Number(a.strip_share_count || 0) > 0).length },
          { label: 'Client', value: albums.filter(a => Number(a.client_share_count || 0) > 0).length }
        ]}
      />
      <div className="page-toolbar">
        <PageHelp
          title="How to use Albums"
          description="Group photos into collections for sharing, proofing, and delivery."
          tips={[
            'Create an album to organize a shoot or project.',
            'Open an album to manage its photos and sharing options.',
            'Use the search bar to find albums by name.'
          ]}
          subject="Photo Harbor Help - Albums"
        />
      </div>
      <div className={isMobile ? "px-3 py-3" : "p-8"}>
        <section className={`page-surface ${isMobile ? 'page-surface--tight' : 'page-surface--padded'}`}>
          {/* Mobile create album */}
          <div className="flex gap-2 mb-4 md:hidden album-create-row">
            <input
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
              placeholder="New album name..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && title && createAlbum()}
            />
            <button
              className="px-4 py-2 text-sm text-white bg-blue-500 rounded-lg whitespace-nowrap disabled:opacity-50"
              disabled={!title}
              onClick={createAlbum}
            >
              Create
            </button>
          </div>
          <div className="mb-4 md:hidden">
            <div className="relative">
              <FiSearch className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
              <input
                className="w-full py-2 pl-10 pr-4 text-sm border border-gray-200 rounded-lg"
                placeholder="Search albums..."
                value={albumQuery}
                onChange={(e) => setAlbumQuery(e.target.value)}
              />
              {albumQuery && (
                <button
                  type="button"
                  className="absolute p-1 text-gray-400 transform -translate-y-1/2 rounded-full hover:bg-gray-100 right-2 top-1/2"
                  aria-label="Clear album search"
                  onClick={() => setAlbumQuery('')}
                >
                  <FiX aria-hidden />
                </button>
              )}
            </div>
          </div>
          <div className="items-center justify-end hidden gap-4 md:flex">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
                <button className={`px-2 py-1 text-sm rounded-lg ${viewMode === 'grid' ? 'bg-white shadow' : ''}`} onClick={() => setViewMode('grid')}>Grid</button>
                <button className={`px-2 py-1 text-sm rounded-lg ${viewMode === 'list' ? 'bg-white shadow' : ''}`} onClick={() => setViewMode('list')}>List</button>
              </div>
              <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
                <button className={`px-2 py-1 text-sm rounded-lg ${sortMode === 'recent' ? 'bg-white shadow' : ''}`} onClick={() => setSortMode('recent')}>Recent</button>
                <button className={`px-2 py-1 text-sm rounded-lg ${sortMode === 'az' ? 'bg-white shadow' : ''}`} onClick={() => setSortMode('az')}>A–Z</button>
              </div>
              <div className="relative">
                <FiSearch className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                <input
                  className="w-56 py-2 pl-10 pr-4 border border-gray-200 rounded-lg"
                  placeholder="Search albums..."
                  value={albumQuery}
                  onChange={(e) => setAlbumQuery(e.target.value)}
                />
                {albumQuery && (
                  <button
                    type="button"
                    className="absolute p-1 text-gray-400 transform -translate-y-1/2 rounded-full hover:bg-gray-100 right-2 top-1/2"
                    aria-label="Clear album search"
                    onClick={() => setAlbumQuery('')}
                  >
                    <FiX aria-hidden />
                  </button>
                )}
              </div>
              <input
                className="w-64 py-2 pl-4 pr-4 border border-gray-200 rounded-lg"
                placeholder="New album title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && title && createAlbum()}
              />
              <button
                className="px-4 py-2 text-white bg-blue-500 rounded-lg"
                disabled={!title}
                onClick={createAlbum}
              >
                Create Album
              </button>
            </div>
          </div>
          {loading ? (
            <SkeletonAlbumGrid count={7} />
          ) : viewMode === 'grid' ? (
            <div className="albums-grid">
              {/* Special Favorites Album */}
              {showFavorites && <AlbumCard isFavorites favoriteCount={favoriteCount} favoriteCover={favoriteCover} />}
              {filteredAlbums.map((a) => (
                <AlbumCard key={a.id} album={a} />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3">Album</th>
                    <th scope="col" className="px-6 py-3">Photos</th>
                    <th scope="col" className="px-6 py-3">Visibility</th>
                    <th scope="col" className="px-6 py-3">Sharing</th>
                    <th scope="col" className="px-6 py-3">Created</th>
                    <th scope="col" className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {showFavorites && (
                    <tr className="bg-white border-b">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 font-semibold text-gray-900">
                          <FaHeart className="text-[#3e5c58]" />
                          <strong>Favorites</strong>
                        </div>
                      </td>
                      <td className="px-6 py-4">{favoriteCount}</td>
                      <td className="px-6 py-4"><span className="px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-full">automatic</span></td>
                      <td className="px-6 py-4">—</td>
                      <td className="px-6 py-4">—</td>
                      <td className="px-6 py-4"><Link className="px-3 py-1 text-xs font-medium text-center text-white bg-blue-500 rounded-lg hover:bg-blue-600" to="/favorites">Open</Link></td>
                    </tr>
                  )}
                  {filteredAlbums.map((a) => (
                    <tr key={a.id} className="bg-white border-b">
                      <td className="px-6 py-4 font-semibold text-gray-900"><strong>{a.title}</strong></td>
                      <td className="px-6 py-4">{a.image_count}</td>
                      <td className="px-6 py-4">{albumVisibilityLabel(a) ? <span className="px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-full">{albumVisibilityLabel(a)}</span> : '—'}</td>
                      <td className="px-6 py-4">{renderAlbumShareBadges(a) || '—'}</td>
                      <td className="px-6 py-4">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-6 py-4"><Link className="px-3 py-1 text-xs font-medium text-center text-white bg-blue-500 rounded-lg hover:bg-blue-600" to={`/albums/${a.id}`}>Open</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filteredAlbums.length === 0 && (albumQuery.trim() || (albums.length === 0 && favoriteCount === 0)) && (
            <div className="empty-state">
              {albumQuery.trim() ? (
                <>
                  <p className="muted">No albums match "{albumQuery.trim()}"</p>
                  <p className="muted">Try a different name or clear the search.</p>
                </>
              ) : (
                <>
                  <p className="muted">No albums yet</p>
                  <p className="muted">Create your first album to start organizing photos</p>
                </>
              )}
            </div>
          )}
        </section>
      </div>
    </>
  );
}





function AppContent() {
  const [privateScope, setPrivateScope] = useState(() => getPrivateCacheScope().scope);
  useEffect(() => subscribePrivateCacheScope(({scope}) => setPrivateScope(scope)), []);
  const [health, setHealth] = useState(null);
  const [needsCard, setNeedsCard] = useState(false);
  const { user, loading, loadingMessage, logout } = useAuth();
  const location = useLocation();

  // Account gate: new accounts must add a card before the app is usable.
  useEffect(() => {
    if (!user) { setNeedsCard(false); return; }
    let cancelled = false;
    fetch('/api/subscription', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setNeedsCard(!!d.needsCard); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    // Apply theme on initial load (supports auto | light | dark)
    const themeMode = localStorage.getItem('themeMode');
    const root = document.documentElement;
    const body = document.body;
    function setBySystem() {
      const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark-mode', isDark);
      if (body) body.classList.toggle('dark', isDark);
      localStorage.setItem('darkMode', isDark ? 'true' : 'false');
    }
    if (themeMode === 'dark') {
      root.classList.add('dark-mode');
      if (body) body.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else if (themeMode === 'light') {
      root.classList.remove('dark-mode');
      if (body) body.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    } else {
      setBySystem();
    }

    fetch('/api/health')
      .then((r) => r.json())
      .then(setHealth)
      .catch(() => setHealth({ ok: false }));
  }, []);

  // Body class removed - using Tailwind only

  const isFullBleedPage = location.pathname.startsWith('/strip')
    || location.pathname.startsWith('/submissions');
  const mainClasses = isFullBleedPage
    ? 'min-h-screen'
    : 'px-2 py-2 pb-24 sm:px-4 sm:py-4 md:pb-8 lg:p-8';

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-blue-500"></div>
        <p className="ml-4">{loadingMessage || 'Loading...'}</p>
      </div>
    );
  }

  const isPublicGallery = location.pathname.startsWith('/share')
    || location.pathname.startsWith('/client')
    || location.pathname.startsWith('/claim/');

  // Hard-wall new accounts that haven't added a card (public galleries bypass).
  if (user && !isPublicGallery && needsCard) {
    return <Paywall />;
  }

  const toneClass = (() => {
    const path = location.pathname || '/';
    if (path.startsWith('/settings')) return 'app-tone-8';
    if (path.startsWith('/watermark')) return 'app-tone-7';
    if (path.startsWith('/submissions')) return 'app-tone-6';
    if (path.startsWith('/shares')) return 'app-tone-5';
    if (path.startsWith('/strip')) return 'app-tone-4';
    if (path.startsWith('/friends')) return 'app-tone-3';
    if (path.startsWith('/tags')) return 'app-tone-2';
    if (path.startsWith('/albums')) return 'app-tone-1';
    return 'app-tone-0';
  })();

  return (
    <div className={`flex min-h-screen bg-gray-50${isPublicGallery ? '' : ' app-shell'}`}>
      {user && <AppRail />}
      <div className="flex-1 min-w-0">
        {/* Only show top nav when authenticated and not on login route */}
        {user && location.pathname !== '/login' && <Nav />}
        {user && <StorageWarningBanner />}
        {user && <MobileNav />}
        <main className={`${mainClasses}${isPublicGallery ? '' : ` app-main ${toneClass}`}`}>
            <RouteTransition key={privateScope || "anonymous"}>
              <Routes>
                <Route path="/claim/:token" element={<ProviderClaim />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/security" element={<Security />} />
                <Route path="/features" element={<DaileyCloud />} />
                <Route path="/analogtalk" element={<AnalogTalk />} />
                {/* /you-are-the-product removed for public launch */}
                <Route path="/guide" element={<Guide />} />
                <Route path="/share/:token" element={<PublicGallery />} />
                <Route path="/client/:shareId" element={<ClientGallery />} />
                <Route path="/" element={
                  <ProtectedRoute>
                    <AllPhotos />
                  </ProtectedRoute>
                } />
                <Route path="/photos" element={
                  <ProtectedRoute>
                    <AllPhotos />
                  </ProtectedRoute>
                } />
                <Route path="/albums" element={
                  <ProtectedRoute>
                    <Albums />
                  </ProtectedRoute>
                } />
                <Route path="/albums/:id" element={
                  <ProtectedRoute>
                    <AlbumDetail />
                  </ProtectedRoute>
                } />
                <Route path="/favorites" element={
                  <ProtectedRoute>
                    <Favorites />
                  </ProtectedRoute>
                } />
                <Route path="/shares" element={
                  <ProtectedRoute>
                    <SharesManager />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                {/* Developers routes removed */}
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/watermark" element={
                  <ProtectedRoute>
                    <Watermark />
                  </ProtectedRoute>
                } />
                <Route path="/tags" element={
                  <ProtectedRoute>
                    <Tags />
                  </ProtectedRoute>
                } />
                <Route path="/friends" element={
                  <ProtectedRoute>
                    <Friends />
                  </ProtectedRoute>
                } />
                <Route path="/submissions" element={
                  <ProtectedRoute>
                    <Submissions />
                  </ProtectedRoute>
                } />
                <Route path="/jobs/:jobId" element={
                  <ProtectedRoute>
                    <JobDetail />
                  </ProtectedRoute>
                } />
                <Route path="/strip" element={
                  <ProtectedRoute>
                    <TheStrip />
                  </ProtectedRoute>
                } />
                <Route path="/archive" element={
                  <ProtectedRoute>
                    <ScanArchive />
                  </ProtectedRoute>
                } />
                <Route path="/archive/year/:yearId" element={
                  <ProtectedRoute>
                    <ScanArchive />
                  </ProtectedRoute>
                } />
                <Route path="/archive/month/:monthId" element={
                  <ProtectedRoute>
                    <ScanArchiveMonth />
                  </ProtectedRoute>
                } />
                <Route path="/archive/folder/:folderId" element={
                  <ProtectedRoute>
                    <ScanArchiveFolder />
                  </ProtectedRoute>
                } />
                <Route path="/vault" element={
                  <ProtectedRoute>
                    <Vault />
                  </ProtectedRoute>
                } />
                <Route path="/vault/:id" element={
                  <ProtectedRoute>
                    <VaultCollection />
                  </ProtectedRoute>
                } />
                <Route path="*" element={<Placeholder title="Not Found">No such page.</Placeholder>} />
              </Routes>
            </RouteTransition>

            <AlbumUploadButton key={privateScope} user={user} />

        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TeamProvider>
          <UploadProvider>
          <SharedUploadProvider>
          <UpdateNotification />
          <UploadIndicator />
          <ScrollToTop />
          <BugReportButton />
          {/* Public share route renders full-bleed outside the container */}
          <Routes>
            {/* Landing page for unauthenticated visitors */}
            <Route path="/welcome" element={<LandingPage />} />
            {/* Login should render outside the app shell for proper centering */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/redeem" element={<RedeemPromo />} />
            {/* Public CORE info page (no app shell) */}
            <Route path="/core" element={<CoreInfo />} />
            <Route path="/share/:token" element={<PublicGallery />} />
            <Route path="/client/:shareId" element={<ClientGallery />} />
            {/* Client Portal (token-based access) */}
            <Route path="/c/:token" element={<ClientPortal />} />
            <Route path="/g/:token" element={<ClientGalleryViewer />} />
            <Route path="/g/:token/gallery" element={<ClientGalleryViewer />} />
            <Route path="/s/:token/select" element={<ClientSelectionFlow />} />
            {/* All other routes use the standard app shell */}
            <Route path="/*" element={<AppContent />} />
          </Routes>
          </SharedUploadProvider>
          </UploadProvider>
        </TeamProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
